import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import Logo from "@/components/common/Logo";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const showDemo = process.env.REACT_APP_SHOW_DEMO_CREDENTIALS === "true";

  const onSubmit = async (e) => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      toast.error("Please enter your email address.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!password) {
      toast.error("Please enter your password.");
      return;
    }

    setLoading(true);

    try {
      await login(normalizedEmail, password);

      toast.success("Login successful", {
        description: "Welcome back to Miledeep Help Desk.",
      });

      navigate("/dashboard");
    } catch (err) {
      const status = err?.response?.status;

      if (status === 400 || status === 401 || status === 403) {
        toast.error("Unable to sign in", {
          description:
            "The email address or password is incorrect. Please check your credentials and try again.",
        });
      } else if (!err?.response) {
        toast.error("Connection failed", {
          description:
            "Unable to reach the server. Please check your internet connection and try again.",
        });
      } else {
        toast.error("Login failed", {
          description:
            err?.response?.data?.detail ||
            "An unexpected server error occurred. Please try again.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] bg-card">
      {/* Left: Login form */}
      <div className="flex items-center justify-center p-8 lg:p-14 order-2 lg:order-1">
        <div className="w-full max-w-md space-y-8 animate-fade-up">
          <Logo size={44} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              Miledeep Works · Help Desk CRM
            </div>
            <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">
              Support, <span className="gold-text">refined</span>.
            </h1>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Signin to raise tickets, approve requests, track SLAs and resolve issues across every Miledeep application.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[11px] uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input
                id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email"
                className="mt-1 bg-secondary/50 border-border focus-visible:ring-primary/40"
              />
            </div>
            <div>
              <Label
                htmlFor="password"
                className="text-[11px] uppercase tracking-widest text-muted-foreground"
              >
                Password
              </Label>

              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="login-password"
                  className="pr-12 bg-secondary/50 border-border focus-visible:ring-primary/40"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-muted-foreground transition-colors hover:text-primary focus:outline-none focus:text-primary"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
            <Button
              type="submit" disabled={loading} data-testid="login-submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="border-t border-border pt-4 text-[11px] text-muted-foreground leading-relaxed">
            <div className="uppercase tracking-widest text-muted-foreground">Need an account?</div>
            <div className="mt-2">Contact your Miledeep Works administrator to be provisioned with a role and credentials.</div>
          </div>
          {showDemo && (
            <div className="border-t border-border pt-4 text-[11px] text-muted-foreground leading-relaxed" data-testid="demo-credentials">
              <div className="uppercase tracking-widest text-muted-foreground mb-2">Demo accounts</div>
              <div className="grid grid-cols-2 gap-1 font-mono">
                <div>admin@miledeep.com</div><div className="text-right">MileDeep@2026</div>
                <div>support.admin@miledeep.com</div><div className="text-right">Miledeep@2026</div>
                <div>engineer@miledeep.com</div><div className="text-right">Miledeep@2026</div>
                <div>business.admin@miledeep.com</div><div className="text-right">Miledeep@2026</div>
                <div>user@miledeep.com</div><div className="text-right">Miledeep@2026</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right: Brand backdrop */}
      <div
        className="hidden lg:block relative order-1 lg:order-2 overflow-hidden"
        style={{ background: "linear-gradient(135deg, #eaf3ff 0%, #e8f8fb 100%)" }}
      >
        <div className="relative z-10 flex flex-col justify-between h-full p-14">
          <Logo size={40} />
          <div className="space-y-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-primary/70">
              Enterprise Help Desk · SLA · Approvals · Audit
            </div>
            <h2 className="font-display font-extrabold text-4xl xl:text-5xl leading-[1.05] max-w-md">
              One <span className="gold-text">unified</span> workspace for every Miledeep support request.
            </h2>
            <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
              Structured workflows across six roles. Configurable SLAs. Audit-grade
              history. Email &amp; WhatsApp alerts. Delivered in the Miledeep aesthetic.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 max-w-md">
            {[
              { k: "32", v: "Ticket states" },
              { k: "6", v: "Roles" },
              { k: "SLA", v: "Live tracking" },
            ].map((s) => (
              <div key={s.v} className="glass rounded-lg p-3">
                <div className="font-display text-2xl gold-text font-bold">{s.k}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
